import React, { Fragment, memo } from 'react'
import tw from 'twin.macro'
import { useFormikContext } from 'formik'
import { Typography } from '@mui/material'
import { Button } from 'src/components/shared/Button'
import { FormikSelect } from 'src/components/shared/form/formik/FormikSelect'
import { FormikFileInput } from 'src/components/shared/form/formik/FormikFileInput'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import {
  BUSINESS_UPLOAD_FILE_SIZE_LIMIT,
  SUPPLEMENTAL_DATA_TYPE_OPTIONS,
  SUPPLEMENTAL_FILE_ACCEPT_TYPES,
  SUPPLEMENTAL_FILE_CAPTION,
} from 'src/constants/oatfi-business/constants'
import { SupplementalDataType } from 'src/constants/oatfi-business/enums'
import type { SupplementalDataFormValues } from './SupplementalDataForm.interface'

interface Props {
  isUploading: boolean
}

/**
 * Neither `FormikSelect` nor `FormikTextField` reaches a MUI component that would draw its own
 * asterisk — both render the label in a plain `Typography`. Baking the marker into the label
 * string is what actually surfaces it, without touching either shared component.
 */
const requiredLabel = (text: string) => {
  return `${text} *`
}

const SupplementalDataFormContentComponent = ({ isUploading }: Props) => {
  const { values } = useFormikContext<SupplementalDataFormValues>()

  const isInvoiceType = values.type === SupplementalDataType.Invoice

  return (
    <Fragment>
      <div css={tw`mb-3`}>
        <FormikSelect<SupplementalDataFormValues>
          name="type"
          label={requiredLabel('Document type')}
          options={SUPPLEMENTAL_DATA_TYPE_OPTIONS}
          placeholder="Select a type"
        />
      </div>

      {/* Shown only for the INVOICE type, matching the backend's @ValidateIf. It is a free-text
          UUID rather than a picker over the customer's invoices: the Invoice tab's list endpoint
          is server-paginated and filters historical invoices out entirely, so a select could not
          offer every valid id and would read as "this invoice does not exist" for the omitted
          ones. */}
      {isInvoiceType ? (
        <div css={tw`mb-3`}>
          <FormikTextField<SupplementalDataFormValues>
            name="invoiceId"
            label={requiredLabel('Invoice ID')}
            placeholder="00000000-0000-0000-0000-000000000000"
          />
        </div>
      ) : (
        <Fragment />
      )}

      {/* `acceptTypes` is narrowed to PDF to match the backend's
          `@GigsafeUploadedFile({ fileType: 'pdf' })`, and `caption` is passed so the picker does
          not advertise the PNG/JPG the default wording implies. The size cap is a product
          decision rather than a wire constraint — the API documents none — and the caption is
          built from the same constant so the copy cannot drift from the check. */}
      <div css={tw`mb-4`}>
        <Typography css={tw`mb-1 text-shades-400`} variant="h6">
          {requiredLabel('File')}
        </Typography>
        <FormikFileInput<SupplementalDataFormValues>
          name="file"
          acceptTypes={SUPPLEMENTAL_FILE_ACCEPT_TYPES}
          caption={SUPPLEMENTAL_FILE_CAPTION}
          fileSizeLimit={BUSINESS_UPLOAD_FILE_SIZE_LIMIT}
          inputDataTestId="supplemental-data-file-input"
        />
      </div>

      <div css={tw`flex flex-row justify-end`}>
        <Button
          type="submit"
          size="small"
          color="dark"
          className={tw`px-4`}
          loading={isUploading}
          disabled={isUploading}
          data-testid="supplemental-data-submit"
        >
          Upload document
        </Button>
      </div>
    </Fragment>
  )
}

export const SupplementalDataFormContent = memo(SupplementalDataFormContentComponent)
